import axios from "axios";
import { getAttrib } from "@/lib/attribution";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const http = axios.create({
  baseURL: API,
  withCredentials: true,
  // Serialize array params as repeated keys (e.g. commitment_type=micro&commitment_type=one_off)
  // — matches FastAPI's List[str] Query default. Axios' default emits foo[]=x which FastAPI ignores.
  paramsSerializer: { indexes: null },
});

// Attach Bearer token (fallback to cookie). Used when cookie domain quirks block writes.
http.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Phase 8: any endpoint 403ing with account_suspended means the user was
// banned mid-session — nudge AuthContext to refresh so the suspended
// full-screen view takes over.
http.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 403 && err.response?.data?.detail === "account_suspended") {
      window.dispatchEvent(new Event("civic:suspended"));
    }
    return Promise.reject(err);
  }
);

export const api = {
  me: () => http.get("/auth/me").then(r => r.data),
  postSession: (sessionId) => {
    const ref = (typeof sessionStorage !== "undefined" && sessionStorage.getItem("ref_code")) || undefined;
    return http.post("/auth/session", { session_id: sessionId, ref, attrib: getAttrib() }, {
      headers: { "X-Session-ID": sessionId },
    }).then(r => { if (ref) sessionStorage.removeItem("ref_code"); return r.data; });
  },
  devSession: (email) => {
    const ref = (typeof sessionStorage !== "undefined" && sessionStorage.getItem("ref_code")) || undefined;
    return http.post("/auth/dev-session", { email, ref, attrib: getAttrib() }).then(r => { if (ref) sessionStorage.removeItem("ref_code"); return r.data; });
  },
  logout: () => http.post("/auth/logout").then(r => r.data),
  updateProfile: (p) => http.put("/auth/profile", p).then(r => r.data),

  // Phase 14 — email/password auth + admin OTP step-up
  register: (p) => {
    const ref = (typeof sessionStorage !== "undefined" && sessionStorage.getItem("ref_code")) || undefined;
    return http.post("/auth/register", { ...p, ref, attrib: getAttrib() }).then(r => { if (ref) sessionStorage.removeItem("ref_code"); return r.data; });
  },
  login: (email, password) => http.post("/auth/login", { email, password }).then(r => r.data),
  confirmAge: () => http.post("/auth/confirm-age", { age_16_plus: true }).then(r => r.data),
  myNextStep: () => http.get("/me/next-step").then(r => r.data),
  askLadder: () => http.get("/me/ask-ladder").then(r => r.data),
  leaderWorklist: () => http.get("/me/leader-worklist").then(r => r.data),
  myAreaProgress: () => http.get("/me/area-progress").then(r => r.data),
  resetRequest: (email) => http.post("/auth/reset/request", { email }).then(r => r.data),
  resetConfirm: (token, password) => http.post("/auth/reset/confirm", { token, password }).then(r => r.data),
  verifyEmail: (token) => http.post("/auth/verify-email", { token }).then(r => r.data),
  resendVerifyEmail: () => http.post("/auth/verify-email/resend").then(r => r.data),
  adminOtpSend: () => http.post("/auth/admin/otp/send").then(r => r.data),
  adminOtpVerify: (code) => http.post("/auth/admin/otp/verify", { code }).then(r => r.data),
  totpSetupStart: () => http.post("/auth/2fa/setup/start").then(r => r.data),
  totpSetupVerify: (code) => http.post("/auth/2fa/setup/verify", { code }).then(r => r.data),
  adminTotpVerify: (code) => http.post("/auth/admin/totp/verify", { code }).then(r => r.data),
  adminReviewCounts: () => http.get("/admin/review-counts").then(r => r.data),
  // Challenges (2026-02)
  challenges: () => http.get("/challenges").then(r => r.data),
  challenge: (slug) => http.get(`/challenges/${slug}`).then(r => r.data),
  challengeMe: (id) => http.get(`/challenges/${id}/me`).then(r => r.data),
  challengeJoin: (id, teamId) => http.post(`/challenges/${id}/join`, { team_id: teamId || null }).then(r => r.data),
  challengeLeave: (id, teamId) => http.post(`/challenges/${id}/leave`, { team_id: teamId || null }).then(r => r.data),
  adminChallenges: () => http.get("/admin/challenges").then(r => r.data),
  adminChallengeCreate: (payload) => http.post("/admin/challenges", payload).then(r => r.data),
  adminChallengeUpdate: (id, payload) => http.put(`/admin/challenges/${id}`, payload).then(r => r.data),
  adminChallengeActivate: (id) => http.post(`/admin/challenges/${id}/activate`).then(r => r.data),
  adminChallengeDeactivate: (id) => http.post(`/admin/challenges/${id}/deactivate`).then(r => r.data),
  adminChallengeDelete: (id) => http.delete(`/admin/challenges/${id}`).then(r => r.data),
  adminChallengeDeclareWinners: (id) => http.post(`/admin/challenges/${id}/declare-winners`).then(r => r.data),
  adminChallengePublishRecap: (id) => http.post(`/admin/challenges/${id}/publish-recap`).then(r => r.data),
  promoteAdmin: (userId) => http.post(`/admin/users/${userId}/promote`).then(r => r.data),
  demoteAdmin: (userId) => http.post(`/admin/users/${userId}/demote`).then(r => r.data),

  listOpportunities: (params) => http.get("/opportunities", { params }).then(r => r.data),
  // Phase 18-A1 — quick wins + org CSV bridges
  quickWins: () => http.get("/opportunities/quick-wins").then(r => r.data),
  orgCsvPreview: (csvText) => http.post("/org/opportunities/import/preview", { csv: csvText }).then(r => r.data),
  orgCsvPublish: (csvText) => http.post("/org/opportunities/import/publish", { csv: csvText }).then(r => r.data),
  orgCsvExport: () => http.get("/org/opportunities/export.csv", { responseType: "blob" }).then(r => r.data),
  getOpportunity: (id) => http.get(`/opportunities/${id}`).then(r => r.data),
  myOpportunities: () => http.get("/opportunities/mine").then(r => r.data),
  createOpportunity: (p) => http.post("/opportunities", p).then(r => r.data),
  apply: (id, note, party_size = 0, attest = null, group_size = null) => http.post(`/opportunities/${id}/apply`, { ...(note ? { note } : {}), ...(party_size ? { party_size } : {}), ...(group_size ? { group_size } : {}), ...(attest || {}) }).then(r => r.data),
  updateMyApplication: (id, payload) => http.patch(`/opportunities/${id}/my-application`, payload).then(r => r.data),
  myApplications: () => http.get("/applications/mine").then(r => r.data),
  orgApplications: () => http.get("/applications/for-org").then(r => r.data),

  // Feedback
  createFeedback: (payload) => http.post("/feedback", payload).then(r => r.data),
  feedbackForUser: (userId) => http.get(`/feedback/for-user/${userId}`).then(r => r.data),
  feedbackMine: () => http.get("/feedback/mine").then(r => r.data),
  feedbackPending: () => http.get("/feedback/pending").then(r => r.data),

  // Phase 3B: streaks + referrals + city leaderboard
  checkIn: () => http.post("/users/me/check-in").then(r => r.data),
  myReferral: () => http.get("/users/me/referral").then(r => r.data),
  areaDensity: () => http.get("/users/me/area-density").then(r => r.data),
  trackReferralShare: (source) => http.post("/users/me/referral/share", { source }).then(r => r.data),
  referrerWelcome: () => http.get("/users/me/referrer-welcome").then(r => r.data),
  ackReferrerWelcome: () => http.post("/users/me/referrer-welcome/ack").then(r => r.data),
  adminLaunchHealth: (fresh = false) =>
    http.get("/admin/launch-health", fresh ? { headers: { "Cache-Control": "no-cache" } } : undefined).then(r => r.data),
  adminBroadcastAudienceCount: (audience) => http.get(`/admin/broadcasts/audience-count?audience=${audience}`).then(r => r.data),
  adminBroadcasts: () => http.get("/admin/broadcasts").then(r => r.data),
  adminSendBroadcast: (payload) => http.post("/admin/broadcasts", payload).then(r => r.data),
  adminRecallBroadcast: (id) => http.post(`/admin/broadcasts/${id}/recall`).then(r => r.data),
  citiesLeaderboard: (period = "month") => http.get("/leaderboard/cities", { params: { period } }).then(r => r.data),

  logHours: (p) => http.post("/hours", p).then(r => r.data),
  myHours: () => http.get("/hours/mine").then(r => r.data),
  pendingHours: () => http.get("/hours/pending").then(r => r.data),
  hoursByStatus: (status) => http.get(`/hours/pending?status=${status}`).then(r => r.data),
  approveHours: (id) => http.post(`/hours/${id}/approve`).then(r => r.data),
  bulkApproveHours: (log_ids) => http.post("/hours/bulk-approve", { log_ids }).then(r => r.data),
  orgHourStats: () => http.get("/hours/org-stats").then(r => r.data),
  exportHoursCsv: () => http.get("/hours/export.csv", { responseType: "blob" }).then(r => r.data),
  // GET /me/story-nudge is no longer called from the client — the story prompt
  // is served as a rung of /me/next-step since the nudge consolidation. The
  // endpoint still exists and is covered by test_story_nudge.py.
  dismissStoryNudge: (id) => http.post(`/me/story-nudge/${id}/dismiss`).then(r => r.data),
  starterChecklist: () => http.get("/me/starter-checklist").then(r => r.data),
  rejectHours: (id) => http.post(`/hours/${id}/reject`).then(r => r.data),

  myStats: () => http.get("/me/stats").then(r => r.data),
  myWrapped: () => http.get("/users/me/wrapped").then(r => r.data),
  latestCelebration: () => http.get("/public/latest-celebration").then(r => r.data),
  publicStats: () => http.get("/public/stats").then(r => r.data),
  localPreview: (postcode) => http.get("/public/local-preview", { params: { postcode } }).then(r => r.data),

  sendMessage: (to_user_id, content, image = null) =>
    http.post("/messages", { to_user_id, content, image }).then(r => r.data),
  threads: () => http.get("/messages/threads").then(r => r.data),
  thread: (otherId, opts = {}) => http.get(`/messages/thread/${otherId}`, { params: opts.markRead ? { mark_read: true } : undefined }).then(r => r.data),
  markThreadRead: (otherId) => http.post(`/messages/thread/${otherId}/mark-read`).then(r => r.data),

  publicUser: (id) => http.get(`/users/${id}`).then(r => r.data),
  publicProfile: (id) => http.get(`/users/${id}/profile`).then(r => r.data),

  notifications: () => http.get("/notifications").then(r => r.data),
  markRead: (id) => http.post(`/notifications/${id}/read`).then(r => r.data),
  markAllRead: () => http.post("/notifications/read-all").then(r => r.data),

  seedDemo: () => http.post("/seed/demo").then(r => r.data),

  uploadImage: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  setAvatar: (picture) => http.put("/auth/avatar", { picture }).then(r => r.data),
  calendarEvents: (weeks = 8) => http.get(`/opportunities/calendar/events?weeks=${weeks}`).then(r => r.data),
  calendarFeedInfo: () => http.get("/calendar/feed-info").then(r => r.data),
  setCalendarFeedPrefs: (alarms) => http.put("/calendar/feed-prefs", { alarms }).then(r => r.data),
  regenerateCalendarFeed: () => http.post("/calendar/feed/regenerate").then(r => r.data),
  orgAnalytics: () => http.get("/org/analytics").then(r => r.data),
  matchedForMe: () => http.get("/opportunities/matched/mine").then(r => r.data),
  exportCsvUrl: () => `${API}/org/analytics/export.csv`,

  // Verification / admin
  requestVerification: (p) => http.post("/org/verify/request", p).then(r => r.data),
  adminVerifications: () => http.get("/admin/verifications").then(r => r.data),
  approveVerification: (id) => http.post(`/admin/verifications/${id}/approve`).then(r => r.data),
  rejectVerification: (id) => http.post(`/admin/verifications/${id}/reject`).then(r => r.data),

  // Endorsements
  endorseNeighbour: (userId) => http.post(`/users/${userId}/endorsement`).then(r => r.data),
  unendorseNeighbour: (userId) => http.delete(`/users/${userId}/endorsement`).then(r => r.data),
  endorseSkill: (userId, skill) => http.post(`/users/${userId}/endorse`, { skill }).then(r => r.data),
  unendorseSkill: (userId, skill) => http.delete(`/users/${userId}/endorse/${encodeURIComponent(skill)}`).then(r => r.data),

  // Teams
  createTeam: (name, team_type = "friends") => http.post("/teams", { name, team_type }).then(r => r.data),
  myTeams: () => http.get("/teams/mine").then(r => r.data),
  inviteToTeam: (teamId, email) => http.post(`/teams/${teamId}/invite`, { email }).then(r => r.data),
  respondTeamInvite: (teamId, accept) => http.post(`/teams/${teamId}/respond`, { accept }).then(r => r.data),
  applyAsTeam: (teamId, opportunity_id) => http.post(`/teams/${teamId}/apply`, { opportunity_id }).then(r => r.data),

  // Public org
  orgPublic: (orgId) => http.get(`/orgs/${orgId}/public`).then(r => r.data),
  orgPublicFeedback: (orgId) => http.get(`/feedback/org/${orgId}/public`).then(r => r.data),
  orgRatings: (ids) => http.get("/feedback/org-ratings", { params: { ids: ids.join(",") } }).then(r => r.data),
  orgResponseRates: (ids) => http.get("/orgs/response-rates", { params: { ids: ids.join(",") } }).then(r => r.data),
  featuredOrgs: () => http.get("/public/featured-orgs").then(r => r.data),

  // Swipe / discover
  swipeDeck: () => http.get("/swipes/deck").then(r => r.data),
  orgSwipeDeck: () => http.get("/swipes/org-deck").then(r => r.data),
  swipe: (target_id, direction) => http.post("/swipes", { target_id, direction }).then(r => r.data),
  savedSwipes: () => http.get("/swipes/saved").then(r => r.data),
  unsave: (targetId) => http.delete(`/swipes/${encodeURIComponent(targetId)}`).then(r => r.data),

  // Stories
  stories: (limit = 30, storyType, featured, scope) => http.get(`/stories?limit=${limit}${storyType ? `&story_type=${storyType}` : ""}${featured ? "&featured=true" : ""}${scope ? `&scope=${scope}` : ""}`).then(r => r.data),
  storyComments: (id) => http.get(`/stories/${id}/comments`).then(r => r.data),
  addStoryComment: (id, text) => http.post(`/stories/${id}/comments`, { text }).then(r => r.data),
  deleteStoryComment: (storyId, commentId) => http.delete(`/stories/${storyId}/comments/${commentId}`).then(r => r.data),
  adminFeatureStory: (id, featured) => http.post(`/admin/stories/${id}/feature`, { featured }).then(r => r.data),
  story: (id) => http.get(`/stories/${id}`).then(r => r.data),
  postStory: (p) => http.post("/stories", p).then(r => r.data),
  likeStory: (id) => http.post(`/stories/${id}/like`).then(r => r.data),
  pinStory: (id) => http.post(`/stories/${id}/pin`).then(r => r.data),
  unpinStory: (id) => http.post(`/stories/${id}/unpin`).then(r => r.data),
  orgPinnedStories: (orgId) => http.get(`/orgs/${orgId}/pinned-stories`).then(r => r.data),
  deleteStory: (id) => http.delete(`/stories/${id}`).then(r => r.data),

  // Leaderboard
  leaderboard: (scope = "national", lat = null, lng = null) =>
    http.get("/leaderboard", { params: { scope, ...(lat != null ? { lat, lng } : {}) } }).then(r => r.data),

  // Partners
  partnerApply: (p) => http.post("/partners/apply", p).then(r => r.data),

  // Admin invites / outreach
  adminInvites: () => http.get("/admin/invites").then(r => r.data),
  createInvite: (label) => http.post("/admin/invites", { label }).then(r => r.data),
  deleteInvite: (id) => http.delete(`/admin/invites/${id}`).then(r => r.data),

  authConfig: () => http.get("/auth/config").then(r => r.data),

  // Explore & awards
  exploreCounts: () => http.get("/public/explore-counts").then(r => r.data),
  experiences: (limit = 6) => http.get(`/public/experiences?limit=${limit}`).then(r => r.data),
  causeBadges: () => http.get("/awards/causes").then(r => r.data),
  myAwardProgress: () => http.get("/awards/my-progress").then(r => r.data),

  // Awareness days, content & featured
  awarenessDays: (limit = 6) => http.get(`/public/awareness-days?limit=${limit}`).then(r => r.data),
  contentList: (limit = 30) => http.get(`/public/content?limit=${limit}`).then(r => r.data),
  contentGet: (id) => http.get(`/public/content/${id}`).then(r => r.data),
  adminCreateContent: (p) => http.post("/admin/content", p).then(r => r.data),
  adminDeleteContent: (id) => http.delete(`/admin/content/${id}`).then(r => r.data),
  adminOpportunities: () => http.get("/admin/opportunities").then(r => r.data),
  adminDigestRuns: () => http.get("/admin/digest-runs").then(r => r.data),
  adminRecentEmails: () => http.get("/admin/emails/recent").then(r => r.data),
  adminDigestDryRun: () => http.post("/admin/digest-dry-run").then(r => r.data),
  toggleFeatured: (id, featured) => http.post(`/admin/opportunities/${id}/feature`, { featured }).then(r => r.data),
  featuredOpps: (limit = 4) => http.get(`/public/featured-opps?limit=${limit}`).then(r => r.data),
  setAlertPrefs: (radius_miles) => http.post("/users/me/alerts", { radius_miles }).then(r => r.data),
  setNotificationPrefs: (email_opt_out) => http.post("/users/me/notification-prefs", { email_opt_out }).then(r => r.data),

  submitAppeal: (text) => http.post("/appeals", { text }).then(r => r.data),
  myAppeal: () => http.get("/appeals/mine").then(r => r.data),
  adminAppeals: (status = "pending") => http.get("/admin/appeals", { params: { status } }).then(r => r.data),
  resolveAppeal: (id, action, reason) => http.post(`/admin/appeals/${id}/${action}`, action === "reject" ? { reason } : {}).then(r => r.data),
  auditLogs: (params) => http.get("/admin/audit-logs", { params }).then(r => r.data),

  // Phase 9 — first-party analytics
  sendPageview: (path, session_hash) => http.post("/analytics/pageview", { path, session_hash }).then(r => r.data),
  sendPwaInstall: () => http.post("/analytics/pwa-install", {}).then(r => r.data),
  myConflicts: (params) => http.get("/me/conflicts", { params }).then(r => r.data),
  adminAnalyticsSummary: () => http.get("/admin/analytics/summary").then(r => r.data),
  adminAnalyticsActivity: (days) => http.get("/admin/analytics/activity", { params: { days } }).then(r => r.data),
  adminAnalyticsGrowth: (days) => http.get("/admin/analytics/growth", { params: { days } }).then(r => r.data),
  adminAnalyticsEngagement: (days) => http.get("/admin/analytics/engagement", { params: { days } }).then(r => r.data),
  adminAnalyticsTraffic: (days) => http.get("/admin/analytics/traffic", { params: { days } }).then(r => r.data),
  adminAnalyticsQualityFlags: () => http.get("/admin/analytics/quality-flags").then(r => r.data),

  // Geo search (UK postcodes + places)
  geoSearch: (q) => http.get("/geo/search", { params: { q } }).then(r => r.data),
  // Phase 18-A2 — referrals + campaigns
  referrerMe: () => http.get("/referrers/me").then(r => r.data),
  referrerApply: (p) => http.post("/referrers/apply", p).then(r => r.data),
  createReferral: (p) => http.post("/referrals", p).then(r => r.data),
  repostReferral: (id) => http.post(`/referrals/${id}/repost`).then(r => r.data),
  createReferralInvite: (p) => http.post("/referrals/invites", p).then(r => r.data),
  myReferrals: () => http.get("/referrals/mine").then(r => r.data),
  adminReferrers: () => http.get("/admin/referrers").then(r => r.data),
  approveReferrer: (id) => http.post(`/admin/referrers/${id}/approve`).then(r => r.data),
  rejectReferrer: (id, reason = "") => http.post(`/admin/referrers/${id}/reject`, { reason }).then(r => r.data),
  revokeReferrer: (uid) => http.post(`/admin/referrers/${uid}/revoke`).then(r => r.data),
  referralAnalytics: () => http.get("/admin/referrals/analytics").then(r => r.data),
  activeCampaign: () => http.get("/campaigns/active").then(r => r.data),
  campaignLanding: (slug) => http.get(`/campaigns/${slug}`).then(r => r.data),
  adminCampaigns: () => http.get("/admin/campaigns").then(r => r.data),
  createCampaign: (p) => http.post("/admin/campaigns", p).then(r => r.data),
  updateCampaign: (id, p) => http.put(`/admin/campaigns/${id}`, p).then(r => r.data),
  activateCampaign: (id) => http.post(`/admin/campaigns/${id}/activate`).then(r => r.data),
  deactivateCampaign: (id) => http.post(`/admin/campaigns/${id}/deactivate`).then(r => r.data),
  deleteCampaign: (id) => http.delete(`/admin/campaigns/${id}`).then(r => r.data),
  tagCampaignEvent: (id, p) => http.post(`/admin/campaigns/${id}/tag`, p).then(r => r.data),
  areasLeaderboard: (period) => http.get("/leaderboard/areas", { params: { period } }).then(r => r.data),
  areaDetail: (area, period) => http.get(`/leaderboard/areas/${encodeURIComponent(area)}`, { params: { period } }).then(r => r.data),
  postAreaChallenge: (area, collectiveId) => http.post(`/leaderboard/areas/${encodeURIComponent(area)}/challenge`, { collective_id: collectiveId }).then(r => r.data),
  submitSiteFeedback: (payload) => http.post("/site-feedback", payload).then(r => r.data),
  // Phase 18-B1 — trust levels + programme kit
  myTrust: () => http.get("/users/me/trust").then(r => r.data),
  recordDbsAttestation: (p) => http.post("/org/dbs-attestations", p).then(r => r.data),
  revokeDbsAttestation: (id) => http.delete(`/org/dbs-attestations/${id}`).then(r => r.data),
  listDbsAttestations: () => http.get("/org/dbs-attestations").then(r => r.data),
  vouchMember: (cid, uid) => http.post(`/collectives/${cid}/vouches/${uid}`).then(r => r.data),
  unvouchMember: (cid, uid) => http.delete(`/collectives/${cid}/vouches/${uid}`).then(r => r.data),
  collectiveVouches: (cid) => http.get(`/collectives/${cid}/vouches`).then(r => r.data),
  collectiveBuddies: (cid) => http.get(`/collectives/${cid}/buddies`).then(r => r.data),
  setBuddy: (cid, memberId, buddyUserId) => http.post(`/collectives/${cid}/buddies/${memberId}`, { buddy_user_id: buddyUserId }).then(r => r.data),
  clearBuddy: (cid, memberId) => http.delete(`/collectives/${cid}/buddies/${memberId}`).then(r => r.data),
  gatheringTemplates: () => http.get("/content/gathering-templates").then(r => r.data),
  // Phase 18-B2 — perks pilot + employer teams + polish
  perks: () => http.get("/perks").then(r => r.data),
  redeemPerk: (id) => http.post(`/perks/${id}/redeem`).then(r => r.data),
  adminPerks: () => http.get("/admin/perks").then(r => r.data),
  setPerksConfig: (areas) => http.put("/admin/perks/config", { areas }).then(r => r.data),
  createPerk: (p) => http.post("/admin/perks", p).then(r => r.data),
  updatePerk: (id, p) => http.put(`/admin/perks/${id}`, p).then(r => r.data),
  deletePerk: (id) => http.delete(`/admin/perks/${id}`).then(r => r.data),
  perkRedemptions: () => http.get("/admin/perks/redemptions").then(r => r.data),
  perkRedemptionsCsv: () => http.get("/admin/perks/redemptions.csv", { responseType: "blob" }).then(r => r.data),
  teamImpact: (id) => http.get(`/teams/${id}/impact`).then(r => r.data),
  teamQuarterly: (id) => http.get(`/teams/${id}/quarterly`).then(r => r.data),
  pendingEmployerTeams: () => http.get("/admin/teams/pending").then(r => r.data),
  approveEmployerTeam: (id) => http.post(`/admin/teams/${id}/approve`).then(r => r.data),
  rejectEmployerTeam: (id) => http.post(`/admin/teams/${id}/reject`).then(r => r.data),
  employerEnquiry: (p) => http.post("/partners/employer-enquiry", p).then(r => r.data),
  adminEnquiryStatus: (id, status) => http.post(`/admin/employer-enquiries/${id}/status`, { status }).then(r => r.data),
  adminEmployerEnquiries: () => http.get("/admin/employer-enquiries").then(r => r.data),
  myHoursCsv: () => http.get("/hours/mine/export.csv", { responseType: "blob" }).then(r => r.data),
  adminSiteFeedback: (params) => http.get("/admin/site-feedback", { params }).then(r => r.data),
  resolveSiteFeedback: (id, status) => http.patch(`/admin/site-feedback/${id}`, { status }).then(r => r.data),

  // Applications lifecycle
  withdraw: (oppId) => http.delete(`/opportunities/${oppId}/apply`).then(r => r.data),
  myApplication: (oppId) => http.get(`/opportunities/${oppId}/my-application`).then(r => r.data),

  // Opportunity management
  updateOpportunity: (id, p) => http.patch(`/opportunities/${id}`, p).then(r => r.data),
  cancelOpportunity: (id) => http.delete(`/opportunities/${id}`).then(r => r.data),
  minDecision: (id, action) => http.post(`/opportunities/${id}/min-decision`, { action }).then(r => r.data),

  // Group chat
  toggleGroupChat: (id, enabled) => http.post(`/opportunities/${id}/group-chat`, { enabled }).then(r => r.data),
  groupMessages: (id) => http.get(`/opportunities/${id}/group-messages`).then(r => r.data),
  sendGroupMessage: (id, content, kind = "text") => http.post(`/opportunities/${id}/group-messages`, { content, kind }).then(r => r.data),

  // Swipe undo / skipped
  swipeUndo: () => http.post("/swipes/undo").then(r => r.data),
  skippedSwipes: () => http.get("/swipes/skipped").then(r => r.data),

  // Team management
  promoteMember: (teamId, memberId) => http.post(`/teams/${teamId}/members/${memberId}/promote`).then(r => r.data),
  removeMember: (teamId, memberId) => http.post(`/teams/${teamId}/members/${memberId}/remove`).then(r => r.data),
  deleteTeam: (teamId) => http.delete(`/teams/${teamId}`).then(r => r.data),

  // Content featured
  toggleContentFeatured: (id, featured) => http.post(`/admin/content/${id}/feature`, { featured }).then(r => r.data),

  // Applicant management (org/host)
  applicants: (oppId) => http.get(`/opportunities/${oppId}/applicants`).then(r => r.data),
  decideApplication: (appId, action, reason = "", note = "") =>
    http.post(`/applications/${appId}/decide`, { action, reason, note }).then(r => r.data),
  bulkMessage: (oppId, content) => http.post(`/opportunities/${oppId}/bulk-message`, { content }).then(r => r.data),

  // Reports & moderation
  report: (target_type, target_id, reason, note = "") =>
    http.post("/reports", { target_type, target_id, reason, note }).then(r => r.data),
  adminSearch: (q) => http.get("/admin/search", { params: { q } }).then(r => r.data),
  adminReports: (status = "open") => http.get("/admin/reports", { params: { status } }).then(r => r.data),

  // Public role badges (2026-02)
  requestRoleBadge: (role, work_email, evidence_link) =>
    http.post("/role-badges/request", { role, work_email, evidence_link }).then(r => r.data),
  myRoleBadge: () => http.get("/role-badges/mine").then(r => r.data),
  adminRoleBadges: (status = "pending") => http.get("/admin/role-badges", { params: { status } }).then(r => r.data),
  adminRoleBadgeHolders: () => http.get("/admin/role-badges/holders").then(r => r.data),
  approveRoleBadge: (id, note = "") => http.post(`/admin/role-badges/${id}/approve`, { note }).then(r => r.data),
  declineRoleBadge: (id, note = "") => http.post(`/admin/role-badges/${id}/decline`, { note }).then(r => r.data),
  revokeRoleBadge: (userId) => http.post(`/admin/role-badges/holders/${userId}/revoke`).then(r => r.data),
  resolveReport: (id, action) => http.post(`/admin/reports/${id}/resolve`, { action }).then(r => r.data),
  toggleBan: (userId) => http.post(`/admin/users/${userId}/ban`).then(r => r.data),
  adminEraseUser: (userId, reason) => http.post(`/admin/users/${userId}/erase`, { reason }).then(r => r.data),

  // GDPR: data export & erasure
  exportData: () => http.get("/auth/me/export").then(r => r.data),
  deleteAccount: () => http.delete("/auth/me").then(r => r.data),

  // Verification decision
  rejectVerification: (orgId, reason, note = "") =>
    http.post(`/admin/verifications/${orgId}/reject`, { reason, note }).then(r => r.data),

  // Collectives
  collectives: (params) => http.get("/collectives", { params }).then(r => r.data),
  myCollectives: () => http.get("/collectives/mine").then(r => r.data),
  getCollective: (id) => http.get(`/collectives/${id}`).then(r => r.data),
  applyCollective: (p) => http.post("/collectives/apply", p).then(r => r.data),
  joinCollective: (id) => http.post(`/collectives/${id}/join`).then(r => r.data),
  leaveCollective: (id) => http.post(`/collectives/${id}/leave`).then(r => r.data),
  collectivePosts: (id) => http.get(`/collectives/${id}/posts`).then(r => r.data),
  createCollectivePost: (id, content) => http.post(`/collectives/${id}/posts`, { content }).then(r => r.data),
  deleteCollectivePost: (id, postId) => http.delete(`/collectives/${id}/posts/${postId}`).then(r => r.data),
  waveAtPost: (id, postId) => http.post(`/collectives/${id}/posts/${postId}/wave`).then(r => r.data),
  // Blocking — for your own peace of mind, distinct from reporting.
  blockUser: (id) => http.post(`/users/${id}/block`).then(r => r.data),
  unblockUser: (id) => http.delete(`/users/${id}/block`).then(r => r.data),
  blockStatus: (id) => http.get(`/users/${id}/block`).then(r => r.data),
  myBlocks: () => http.get("/users/me/blocks").then(r => r.data),
  reportClientError: (body) => http.post("/analytics/client-error", body).then(r => r.data),
  adminClientErrors: (days = 7) => http.get("/admin/client-errors", { params: { days } }).then(r => r.data),
  liquidity: (days = 30) => http.get("/admin/liquidity", { params: { days } }).then(r => r.data),
  leaderHealth: () => http.get("/admin/leader-health").then(r => r.data),
  createLostFound: (id, body) => http.post(`/collectives/${id}/lost-found`, body).then(r => r.data),
  resolveLostFound: (id, postId) => http.post(`/collectives/${id}/lost-found/${postId}/resolve`).then(r => r.data),
  // Shared runs (Phase 19 §2.1)
  collectiveRuns: (id) => http.get(`/collectives/${id}/runs`).then(r => r.data),
  createRun: (id, body) => http.post(`/collectives/${id}/runs`, body).then(r => r.data),
  joinRun: (id, runId, body) => http.post(`/collectives/${id}/runs/${runId}/join`, body).then(r => r.data),
  leaveRun: (id, runId) => http.post(`/collectives/${id}/runs/${runId}/leave`).then(r => r.data),
  claimRun: (id, runId, body) => http.post(`/collectives/${id}/runs/${runId}/claim`, body).then(r => r.data),
  unclaimRun: (id, runId) => http.post(`/collectives/${id}/runs/${runId}/unclaim`).then(r => r.data),
  removeRunOrganiser: (id, runId) => http.post(`/collectives/${id}/runs/${runId}/organiser/remove`).then(r => r.data),
  markRunDone: (id, runId) => http.post(`/collectives/${id}/runs/${runId}/done`).then(r => r.data),
  cancelRun: (id, runId) => http.post(`/collectives/${id}/runs/${runId}/cancel`).then(r => r.data),
  // Polls (Phase 19 §3.1)
  collectivePolls: (id, status) => http.get(`/collectives/${id}/polls`, { params: status ? { status } : {} }).then(r => r.data),
  createPoll: (id, body) => http.post(`/collectives/${id}/polls`, body).then(r => r.data),
  votePoll: (id, pollId, body) => http.post(`/collectives/${id}/polls/${pollId}/vote`, body).then(r => r.data),
  addPollOption: (id, pollId, body) => http.post(`/collectives/${id}/polls/${pollId}/options`, body).then(r => r.data),
  closePoll: (id, pollId) => http.post(`/collectives/${id}/polls/${pollId}/close`).then(r => r.data),
  pollToGathering: (id, pollId, body) => http.post(`/collectives/${id}/polls/${pollId}/to-gathering`, body || {}).then(r => r.data),
  // Thank-yous (Phase 19 §3.3)
  myThankYouPrompts: () => http.get("/me/thank-you-prompts").then(r => r.data),
  skipThankYou: (promptId) => http.post(`/me/thank-you-prompts/${promptId}/skip`).then(r => r.data),
  sendThankYou: (body) => http.post("/thank-yous", body).then(r => r.data),
  // Borrow Board
  borrowItems: (id, q) => http.get(`/collectives/${id}/borrow`, { params: q ? { q } : undefined }).then(r => r.data),
  addBorrowItem: (id, body) => http.post(`/collectives/${id}/borrow`, body).then(r => r.data),
  setBorrowStatus: (id, itemId, body) => http.post(`/collectives/${id}/borrow/${itemId}/status`, body).then(r => r.data),
  askToBorrow: (id, itemId, body) => http.post(`/collectives/${id}/borrow/${itemId}/ask`, body || {}).then(r => r.data),
  markBorrowReturned: (id, itemId) => http.post(`/collectives/${id}/borrow/${itemId}/returned`).then(r => r.data),
  extendLoan: (id, itemId, dueBack) => http.post(`/collectives/${id}/borrow/${itemId}/extend`, { due_back: dueBack }).then(r => r.data),
  deleteBorrowItem: (id, itemId) => http.delete(`/collectives/${id}/borrow/${itemId}`).then(r => r.data),
  // The Little Book
  streetNotes: (id, q, extra) => http.get(`/collectives/${id}/notes`, { params: { ...(q ? { q } : {}), ...(extra || {}) } }).then(r => r.data),
  secondStreetNote: (id, noteId) => http.post(`/collectives/${id}/notes/${noteId}/second`).then(r => r.data),
  addStreetNote: (id, body) => http.post(`/collectives/${id}/notes`, body).then(r => r.data),
  editStreetNote: (id, noteId, body) => http.put(`/collectives/${id}/notes/${noteId}`, body).then(r => r.data),
  deleteStreetNote: (id, noteId) => http.delete(`/collectives/${id}/notes/${noteId}`).then(r => r.data),
  littleBookCouncils: () => http.get("/little-book/councils").then(r => r.data),
  prefillLittleBook: (id, council) => http.post(`/collectives/${id}/notes/prefill`, { council }).then(r => r.data),
  prefillSafety: (id) => http.post(`/collectives/${id}/notes/prefill-safety`).then(r => r.data),
  createMissingPerson: (id, payload) => http.post(`/collectives/${id}/missing-person`, payload).then(r => r.data),
  resolveMissingPerson: (id, postId) => http.post(`/collectives/${id}/missing-person/${postId}/resolve`).then(r => r.data),
  confirmStreetNote: (id, noteId) => http.post(`/collectives/${id}/notes/${noteId}/confirm`).then(r => r.data),
  weeklyPaste: (id) => http.get(`/collectives/${id}/weekly-paste`).then(r => r.data),
  collectiveScoreboard: (id) => http.get(`/collectives/${id}/scoreboard`).then(r => r.data),
  streetWeek: () => http.get("/me/street-week").then(r => r.data),
  askForNeighbour: (body) => http.post("/referrals/for-neighbour", body).then(r => r.data),
  collectiveChat: (id, roomId) => http.get(`/collectives/${id}/chat`, { params: roomId ? { room_id: roomId } : {} }).then(r => r.data),
  sendCollectiveChat: (id, content, roomId) => http.post(`/collectives/${id}/chat`, { content, room_id: roomId || null }).then(r => r.data),
  collectiveRooms: (id) => http.get(`/collectives/${id}/rooms`).then(r => r.data),
  createCollectiveRoom: (id, name) => http.post(`/collectives/${id}/rooms`, { name }).then(r => r.data),
  updateCollectiveRoom: (id, roomId, patch) => http.patch(`/collectives/${id}/rooms/${roomId}`, patch).then(r => r.data),
  togglePinCollectiveMessage: (id, msgId) => http.post(`/collectives/${id}/chat/${msgId}/pin`).then(r => r.data),
  markRoomSeen: (id, roomId) => http.post(`/collectives/${id}/rooms/${roomId}/seen`).then(r => r.data),
  collectiveLocal: (id) => http.get(`/collectives/${id}/local`).then(r => r.data),
  promoteCollectiveMember: (id, memberId) => http.post(`/collectives/${id}/members/${memberId}/promote`).then(r => r.data),
  demoteCollectiveMember: (id, memberId) => http.post(`/collectives/${id}/members/${memberId}/demote`).then(r => r.data),
  removeCollectiveMember: (id, memberId) => http.post(`/collectives/${id}/members/${memberId}/remove`).then(r => r.data),
  adminCollectives: (status = "pending") => http.get("/admin/collectives", { params: { status } }).then(r => r.data),
  // Phase 17.5 — collectives health (dormancy + takeover)
  takeoverContext: (id) => http.get(`/collectives/${id}/takeover-context`).then(r => r.data),
  requestTakeover: (id, motivation) => http.post(`/collectives/${id}/takeover-requests`, { motivation }).then(r => r.data),
  adminTakeovers: () => http.get("/admin/collectives/takeovers").then(r => r.data),
  approveTakeover: (rid) => http.post(`/admin/collectives/takeovers/${rid}/approve`).then(r => r.data),
  declineTakeover: (rid, reason = "") => http.post(`/admin/collectives/takeovers/${rid}/decline`, { reason }).then(r => r.data),
  approveCollective: (id) => http.post(`/admin/collectives/${id}/approve`).then(r => r.data),
  rejectCollective: (id, reason = "") => http.post(`/admin/collectives/${id}/reject`, { reason }).then(r => r.data),

  // Phase 4C — collective leader tools
  collectiveStats: (id) => http.get(`/collectives/${id}/stats`).then(r => r.data),
  collectiveInvite: (id) => http.get(`/collectives/${id}/invite`).then(r => r.data),
  collectiveByInvite: (code) => http.get(`/collectives/by-invite/${code}`).then(r => r.data),
  joinByInvite: (code) => http.post(`/collectives/join-by-invite`, { invite_code: code }).then(r => r.data),
  collectivePending: (id) => http.get(`/collectives/${id}/pending`).then(r => r.data),
  approvePending: (id, userId) => http.post(`/collectives/${id}/pending/${userId}/approve`).then(r => r.data),
  rejectPending: (id, userId, reason = "") => http.post(`/collectives/${id}/pending/${userId}/reject`, { reason }).then(r => r.data),

  // Donations
  donationsConfig: () => http.get("/donations/config").then(r => r.data),
  donationCheckout: (p) => http.post("/donations/checkout", p).then(r => r.data),
  donationStatus: (sessionId) => http.get(`/donations/status/${sessionId}`).then(r => r.data),
  donationSupporters: () => http.get("/donations/supporters").then(r => r.data),

  // Nearby help requests
  nearbyHelp: () => http.get("/opportunities/help/nearby").then(r => r.data),
  myHelpOffers: () => http.get("/opportunities/help/my-offers").then(r => r.data),

  // AI / recommendations
  recommendations: (params = {}) => http.get("/opportunities/recommendations", { params }).then(r => r.data),
  draftHelpRequest: (note) => http.post("/ai/help-request/draft", { note }).then(r => r.data),
  skillsTaxonomy: () => http.get("/skills/taxonomy").then(r => r.data),
  suggestSkill: (skill) => http.post("/skills/suggest", { skill }).then(r => r.data),
  adminSkillSuggestions: (status = "pending") => http.get("/admin/skill-suggestions", { params: { status } }).then(r => r.data),
  resolveSkillSuggestion: (id, action) => http.post(`/admin/skill-suggestions/${id}/${action}`).then(r => r.data),
  extractSkills: (bio, extra_context) => http.post("/users/me/extract-skills", { bio, extra_context }).then(r => r.data),
  adminVerificationNearby: (userId) => http.get(`/admin/verifications/${userId}/nearby-orgs`).then(r => r.data),
  adminVerificationReview: (orgId) => http.get(`/admin/verifications/${orgId}/review`).then(r => r.data),
  updateCollectiveSettings: (id, payload) => http.patch(`/collectives/${id}/settings`, payload).then(r => r.data),
  // gatherings (Phase 3)
  listGatherings: (id, when = "upcoming") => http.get(`/collectives/${id}/gatherings`, { params: { when } }).then(r => r.data),
  createGathering: (id, payload) => http.post(`/collectives/${id}/gatherings`, payload).then(r => r.data),
  rsvpGathering: (gid, going, party_size = 0) => http.post(`/gatherings/${gid}/rsvp`, { going, party_size }).then(r => r.data),
  cancelGathering: (gid) => http.delete(`/gatherings/${gid}`).then(r => r.data),

  // Share cards (server-rendered PNG)
  ogBadgeUrl: (userId) => `${API}/og/badge/${userId}`,
  ogWrappedUrl: (userId) => `${API}/og/wrapped/${userId}`,
  ogStoryUrl: (storyId) => `${API}/og/story/${storyId}`,
  ogAchievementUrl: (userId, achievementId) => `${API}/og/achievement/${userId}/${achievementId}`,
  ogCollectiveUrl: (id) => `${API}/og/collective/${id}`,
  ogOpportunityUrl: (id) => `${API}/og/opportunity/${id}`,
  ogGatheringUrl: (id) => `${API}/og/gathering/${id}`,
  ogPlaceUrl: (slug) => `${API}/og/place/${slug}`,
  // Share-redirect pages — HTML with OG meta so pasted links unfurl in
  // WhatsApp/Meta (kind: story | collective | opportunity | gathering | badge | area)
  shareUrl: (kind, refId) => `${API}/share/${kind}/${refId}`,

  // Phase 17-C — growth
  geoPostcode: (q) => http.get("/geo/postcode", { params: { q } }).then(r => r.data),
  collectiveNotifyInterest: (email, outcode) => http.post("/collectives/notify-interest", { email, outcode }).then(r => r.data),
  // /street front door (collectives brief Phase 1)
  streetSearch: (lat, lng) => http.get("/street/search", { params: { lat, lng } }).then(r => r.data),
  streetDemand: (outcode) => http.post("/street/demand", { outcode }).then(r => r.data),
  launchConfig: () => http.get("/public/launch-config").then(r => r.data),
  launchInterest: (email, outcode) => http.post("/launch-interest", { email, outcode }).then(r => r.data),
  publicAreas: () => http.get("/public/areas").then(r => r.data),
  publicArea: (slug) => http.get(`/public/area/${slug}`).then(r => r.data),
  adminAnalyticsAcquisition: (days) => http.get("/admin/analytics/acquisition", { params: { days } }).then(r => r.data),
  adminAreas: () => http.get("/admin/areas").then(r => r.data),
  adminCreateArea: (name) => http.post("/admin/areas", { name }).then(r => r.data),
  adminDeleteArea: (id) => http.delete(`/admin/areas/${id}`).then(r => r.data),
  adminLaunchAreas: () => http.get("/admin/launch-areas").then(r => r.data),
  adminSetLaunchAreas: (areas) => http.put("/admin/launch-areas", { areas }).then(r => r.data),
  adminCheckOutcodes: (outcodes) => http.post("/admin/launch-areas/check", { outcodes }).then(r => r.data),
  adminCreateCollective: (payload) => http.post("/admin/collectives", payload).then(r => r.data),
  adminInterestSummary: () => http.get("/admin/interest/summary").then(r => r.data),
  adminInterestDemand: () => http.get("/admin/interest/demand").then(r => r.data),
  adminStreetDemand: () => http.get("/admin/street-demand").then(r => r.data),
  adminInterestExportCsv: (kind, area) => http.get("/admin/interest/export.csv", { params: { ...(kind ? { kind } : {}), ...(area ? { area } : {}) }, responseType: "blob" }).then(r => r.data),
  adminLaunchBlastPreview: () => http.get("/admin/interest/launch-blast/preview").then(r => r.data),
  adminLaunchBlastSend: () => http.post("/admin/interest/launch-blast").then(r => r.data),

  // Phase 17-D — council impact + evidence mode
  publicImpact: (slug) => http.get(`/public/impact/${slug}`).then(r => r.data),
  adminImpactExportCsv: (slug) => http.get(`/admin/impact/${slug}/export.csv`, { responseType: "blob" }).then(r => r.data),
  evidenceEligibleOrgs: () => http.get("/evidence/eligible-orgs").then(r => r.data),
  createEvidencePack: (p) => http.post("/evidence/packs", p).then(r => r.data),
  myEvidencePacks: () => http.get("/evidence/packs").then(r => r.data),
  getEvidencePack: (id) => http.get(`/evidence/packs/${id}`).then(r => r.data),
  verifyEvidenceCode: (code) => http.get(`/verify/${code}`).then(r => r.data),
  createAssessorRequest: (org_user_id, note) => http.post("/evidence/assessor-requests", { org_user_id, note }).then(r => r.data),
  assessorRequests: () => http.get("/evidence/assessor-requests").then(r => r.data),
  respondAssessorRequest: (id, action, text) => http.post(`/evidence/assessor-requests/${id}/respond`, { action, text }).then(r => r.data),

  // Celebration moments (Phase 13)
  myCelebrations: () => http.get("/me/celebrations").then(r => r.data),
  ackCelebrations: (ids) => http.post("/me/celebrations/ack", { ids }).then(r => r.data),
  shareAchievementToCollective: (collectiveId, achievementId) =>
    http.post("/me/celebrations/share-to-collective", { collective_id: collectiveId, achievement_id: achievementId }).then(r => r.data),
};
